import React, { useMemo, useState, lazy, Suspense } from "react";
import { Tabs, Tab } from "react-bootstrap";

// Lazy load components outside render so React does not recreate them on every state change.
const RoomTypes = lazy(() => import("../../pages/hms/components/RoomTypes"));
const RoomMaster = lazy(() => import("../../pages/hms/components/RoomMaster"));
const PropertyInfo = lazy(() => import("../../pages/hms/components/PropertyInfo"));
const UserManagement = lazy(() => import("../../pages/hms/components/ManageUsers"));
const FrontTax = lazy(() => import("../../pages/hms/components/TaxManagement"));

const CONFIG_TABS = [
  {
    key: "propertyInfo",
    title: "Property Information",
    icon: "fas fa-building",
    description: "Company profile, contact details, business settings, compliance information, and brand logo.",
    component: PropertyInfo,
  },
  {
    key: "roomTypes",
    title: "Room Types",
    icon: "fas fa-bed",
    description: "Define room categories, occupancy, amenities, images, and booking-engine rate plans.",
    component: RoomTypes,
  },
  {
    key: "roomMaster",
    title: "Room Master",
    icon: "fas fa-hotel",
    description: "Create and manage physical room numbers, statuses, cleaning state, and operational availability.",
    component: RoomMaster,
  },
  {
    key: "taxes",
    title: "Taxes",
    icon: "fas fa-percent",
    description: "Configure front-office taxes and allocation rules for rooms, inclusions, and price bands.",
    component: FrontTax,
  },
  {
    key: "manageUsers",
    title: "Manage Users",
    icon: "fas fa-users",
    description: "Manage staff access, roles, modules, company allocation, status, and password resets.",
    component: UserManagement,
  },
];

const LoadingSpinner = ({ label = "Loading configuration..." }) => (
  <div className="ns-config-loader">
    <div className="ns-config-loader-card">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
      <span>{label}</span>
    </div>
  </div>
);

const PropertyConfiguration = () => {
  const [activeTab, setActiveTab] = useState("propertyInfo");

  const activeConfig = useMemo(
    () => CONFIG_TABS.find((tab) => tab.key === activeTab) || CONFIG_TABS[0],
    [activeTab]
  );

  const ActiveComponent = activeConfig.component;

  const renderTabTitle = (tab) => (
    <span className="ns-config-tab-title">
      <i className={`${tab.icon} ns-config-tab-icon`} />
      <span>{tab.title}</span>
    </span>
  );

  return (
    <div className="page-wrapper-full">
      <style>{`
        .ns-config-page {
          min-height: calc(100vh - 70px);
        }

        .ns-config-hero {
          background:
            radial-gradient(circle at top left, rgba(37, 99, 235, 0.15), transparent 34%),
            linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);
          padding: 20px;
          margin-bottom: 16px;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 18px;
          flex-wrap: wrap;
        }

        .ns-config-eyebrow {
          color: #2563eb;
          font-size: 0.74rem;
          text-transform: uppercase;
          letter-spacing: 0.14em;
          font-weight: 900;
          margin-bottom: 6px;
        }

        .ns-config-title {
          color: #001f5b;
          font-size: 1.45rem;
          line-height: 1.2;
          font-weight: 900;
          margin: 0;
        }

        .ns-config-subtitle {
          color: #64748b;
          font-size: 0.9rem;
          line-height: 1.55;
          margin: 8px 0 0;
          max-width: 860px;
        }

        .ns-config-quick-stats {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }

        .ns-config-stat {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 999px;
          padding: 7px 11px;
          color: #0f172a;
          font-size: 0.82rem;
          font-weight: 900;
          box-shadow: 0 6px 18px rgba(15, 23, 42, 0.05);
          white-space: nowrap;
        }

        .ns-config-stat span {
          width: 9px;
          height: 9px;
          border-radius: 999px;
          display: inline-block;
        }

        .ns-config-shell {
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 20px;
          box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
          overflow: hidden;
        }

        .ns-config-tabs .nav-tabs {
          background: #f8fafc;
          border-bottom: 1px solid rgba(15, 23, 42, 0.08);
          padding: 10px 12px 0;
          gap: 6px;
          flex-wrap: nowrap;
          overflow-x: auto;
          overflow-y: hidden;
          scrollbar-width: thin;
        }

        .ns-config-tabs .nav-tabs .nav-link {
          border: 1px solid transparent !important;
          border-radius: 14px 14px 0 0 !important;
          color: #475569;
          font-weight: 850;
          padding: 11px 14px;
          white-space: nowrap;
          display: inline-flex;
          align-items: center;
          transition: all 0.18s ease;
          background: transparent;
          margin-bottom: 0;
        }

        .ns-config-tabs .nav-tabs .nav-link:hover {
          color: #1d4ed8;
          background: #eff6ff;
          border-color: rgba(37, 99, 235, 0.10) !important;
        }

        .ns-config-tabs .nav-tabs .nav-link.active {
          color: #1d4ed8 !important;
          background: #ffffff !important;
          border-color: rgba(15, 23, 42, 0.08) !important;
          border-bottom-color: #ffffff !important;
          box-shadow: 0 -4px 16px rgba(15, 23, 42, 0.04);
        }

        .ns-config-tab-title {
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .ns-config-tab-icon {
          font-size: 0.92rem;
          opacity: 0.9;
        }


        .ns-config-content {
          background: #f8fafc;
          padding: 14px;
        }

        .ns-config-content-inner {
          min-height: 360px;
        }

        /*
          Child pages already contain their own .content.container-fluid wrapper.
          These rules prevent double spacing and nested page wrappers from looking heavy.
        */
        .ns-config-content-inner > .content.container-fluid,
        .ns-config-content-inner .content.container-fluid {
          padding-left: 0 !important;
          padding-right: 0 !important;
        }

        .ns-config-content-inner > .content.container-fluid {
          padding-top: 0 !important;
          padding-bottom: 0 !important;
        }

        .ns-config-content-inner .page-header:first-child {
          margin-top: 0 !important;
        }

        .ns-config-loader {
          min-height: 320px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f8fafc;
        }

        .ns-config-loader-card {
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 18px;
          box-shadow: 0 10px 26px rgba(15, 23, 42, 0.06);
          padding: 24px 28px;
          display: inline-flex;
          align-items: center;
          gap: 12px;
          color: #64748b;
          font-weight: 900;
        }

        @media (max-width: 768px) {
          .ns-config-hero {
            padding: 16px;
          }

          .ns-config-title {
            font-size: 1.25rem;
          }

          .ns-config-quick-stats {
            justify-content: flex-start;
          }

          .ns-config-tabs .nav-tabs {
            padding-left: 8px;
            padding-right: 8px;
          }

          .ns-config-tabs .nav-tabs .nav-link {
            padding: 10px 12px;
          }

        }
      `}</style>

      <div className="content container-fluid ns-config-page">
        <div className="ns-config-hero">
          <div>
            <div className="ns-config-eyebrow">Property Setup</div>
            <h2 className="ns-config-title">Property Configuration</h2>
            <p className="ns-config-subtitle">
              Manage the core setup for your property, including profile details, room inventory, room types, taxes, and user access from one connected configuration center.
            </p>
          </div>

          <div className="ns-config-quick-stats">
            <span className="ns-config-stat">
              <span style={{ background: "#2563eb" }} />
              Profile
            </span>
            <span className="ns-config-stat">
              <span style={{ background: "#16a34a" }} />
              Rooms
            </span>
            <span className="ns-config-stat">
              <span style={{ background: "#f59e0b" }} />
              Taxes
            </span>
            <span className="ns-config-stat">
              <span style={{ background: "#7c3aed" }} />
              Users
            </span>
          </div>
        </div>

        <div className="ns-config-shell">
          <Tabs
            id="property-config-tabs"
            activeKey={activeTab}
            onSelect={(k) => {
              if (k) setActiveTab(k);
            }}
            className="ns-config-tabs"
            mountOnEnter
            unmountOnExit={false}
          >
            {CONFIG_TABS.map((tab) => (
              <Tab
                key={tab.key}
                eventKey={tab.key}
                title={renderTabTitle(tab)}
              />
            ))}
          </Tabs>


          <div className="ns-config-content">
            <div className="ns-config-content-inner">
              <Suspense fallback={<LoadingSpinner label={`Loading ${activeConfig.title}...`} />}>
                <ActiveComponent />
              </Suspense>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyConfiguration;
